import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  FORECAST_FACTOR_MAX,
  FORECAST_FACTOR_MIN,
  FORECAST_FACTOR_SCOPES,
} from "@erp/shared-types";
import {
  OverrideWindowShape,
  withOverrideWindowRules,
} from "./override-window";
import { PolicyLevelsSchema } from "./demand-overrides.contract";

// Growth/shrinkage factors (im_forecast_adjustment_factors): a buyer scales
// the statistical forecast for one SKU x warehouse by a multiplicative factor
// over a window; the forecast run applies it to every horizon bucket the window
// overlaps, before policy math, so safety stock and the reorder point follow.
// Rows are immutable history: "update" revokes the current row and creates its
// replacement. Products are ProductV3 only (the key is the SKU).
// Plan: docs/procurement/growth-shrinkage-factors.md.

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** Derived from the shared registry; v1 exposes `item_loc` only. */
export const ForecastFactorScopeSchema = z.enum(FORECAST_FACTOR_SCOPES);

export const FORECAST_FACTOR_NO_OP_MESSAGE =
  "A factor of exactly 1 changes nothing; enter a growth or a shrinkage.";

/**
 * Multiplicative: 1.15 plans 15% above the statistical forecast, 0.90 plans
 * 10% below. Same bounds as the table's CHECK constraint; exactly 1 is a no-op
 * row and is rejected.
 */
export const ForecastFactorValueSchema = z
  .number()
  .min(FORECAST_FACTOR_MIN)
  .max(FORECAST_FACTOR_MAX)
  .refine((v) => v !== 1, { message: FORECAST_FACTOR_NO_OP_MESSAGE });

/** One factor row, active or historical. */
export const ForecastFactorRowSchema = z.object({
  id: z.string().uuid(),
  scope: ForecastFactorScopeSchema,
  /** ProductV3 key (the SKU). Null only for a future scope that has no product. */
  productV3Key: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  factor: z.number(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  reviewAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  deactivatedAt: z.string().nullable(),
  deactivatedByUserId: z.string().uuid().nullable(),
  deactivatedByUserName: z.string().nullable(),
  /** The row this one replaced (set by "update"); null for a fresh factor. */
  supersedesOverrideId: z.string().uuid().nullable(),
  /**
   * True iff not revoked and not yet lapsed (`endsAt` null or ahead). Unlike a
   * lead-time override, a factor whose window starts in the future is active:
   * the forecast horizon already reaches its window, so it already scales
   * those buckets.
   */
  isActive: z.boolean(),
  /** True iff active and `reviewAt` has passed. */
  needsReview: z.boolean(),
});
export type ForecastFactorRow = z.infer<typeof ForecastFactorRowSchema>;

/** The series an item_loc factor targets: one SKU (ProductV3 key) at one warehouse. */
const SeriesTargetShape = {
  warehouseId: z.string().uuid(),
  productV3Key: z.string().min(1),
} as const;

// ─── Create ─────────────────────────────────────────────────────────────────

export const CreateForecastFactorInputSchema = withOverrideWindowRules(
  z.object({
    ...SeriesTargetShape,
    /** v1 accepts `item_loc` only; defaults to it so callers may omit it. */
    scope: ForecastFactorScopeSchema.default("item_loc"),
    factor: ForecastFactorValueSchema,
    ...OverrideWindowShape,
    reason: z.string().max(1000).nullable().optional(),
  }),
);

export const CreateForecastFactorResponseSchema = z.object({
  override: ForecastFactorRowSchema,
  /** Whether the series' forecast and policy levels were recomputed with the factor. */
  recomputed: z.boolean(),
});

export type CreateForecastFactorInput = z.infer<
  typeof CreateForecastFactorInputSchema
>;
export type CreateForecastFactorResponse = z.infer<
  typeof CreateForecastFactorResponseSchema
>;

// ─── List (one series, active + history) ────────────────────────────────────

export const ListForecastFactorsInputSchema = z.object(SeriesTargetShape);

export const ListForecastFactorsResponseSchema = z.object({
  /** Newest first, revoked and lapsed rows included. */
  overrides: z.array(ForecastFactorRowSchema),
  /**
   * The newest active row, or null. Several active rows may coexist with
   * different windows; the engine answers per bucket, so this is the headline,
   * not the only row that applies.
   */
  active: ForecastFactorRowSchema.nullable(),
});

export type ListForecastFactorsInput = z.infer<
  typeof ListForecastFactorsInputSchema
>;
export type ListForecastFactorsResponse = z.infer<
  typeof ListForecastFactorsResponseSchema
>;

// ─── Update (revoke + create replacement) ───────────────────────────────────

export const UpdateForecastFactorInputSchema = withOverrideWindowRules(
  z.object({
    overrideId: z.string().uuid(),
    factor: ForecastFactorValueSchema,
    ...OverrideWindowShape,
    reason: z.string().max(1000).nullable().optional(),
  }),
);

export const UpdateForecastFactorResponseSchema = z.object({
  /** The replacement row (now active). */
  override: ForecastFactorRowSchema,
  /** The row it replaced, stamped as revoked by the editor. */
  superseded: ForecastFactorRowSchema,
  recomputed: z.boolean(),
});

export type UpdateForecastFactorInput = z.infer<
  typeof UpdateForecastFactorInputSchema
>;
export type UpdateForecastFactorResponse = z.infer<
  typeof UpdateForecastFactorResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeForecastFactorInputSchema = z.object({
  overrideId: z.string().uuid(),
});

export const RevokeForecastFactorResponseSchema = z.object({
  override: ForecastFactorRowSchema,
  recomputed: z.boolean(),
});

export type RevokeForecastFactorInput = z.infer<
  typeof RevokeForecastFactorInputSchema
>;
export type RevokeForecastFactorResponse = z.infer<
  typeof RevokeForecastFactorResponseSchema
>;

// ─── Preview (before/after, no writes) ──────────────────────────────────────

export const PreviewForecastFactorInputSchema = withOverrideWindowRules(
  z.object({
    ...SeriesTargetShape,
    factor: ForecastFactorValueSchema,
    ...OverrideWindowShape,
    /**
     * The row an edit would replace. Saving revokes it, so "after" leaves it
     * out; "before" keeps it and still matches the board.
     */
    excludeOverrideId: z.string().uuid().nullable().optional(),
  }),
);

export const PreviewForecastFactorResponseSchema = z.object({
  /** Levels with the factors saved today. */
  before: PolicyLevelsSchema,
  /** Levels with the candidate layered on top (newest, so it wins where windows overlap). */
  after: PolicyLevelsSchema,
  /** How many of the horizon's buckets the candidate's window covers. */
  coveredBuckets: z.number().int().nonnegative(),
  horizonBuckets: z.number().int().positive(),
});

export type PreviewForecastFactorInput = z.infer<
  typeof PreviewForecastFactorInputSchema
>;
export type PreviewForecastFactorResponse = z.infer<
  typeof PreviewForecastFactorResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const forecastFactorsContracts = {
  create: oc
    .input(CreateForecastFactorInputSchema)
    .output(CreateForecastFactorResponseSchema)
    .route({
      method: "POST",
      summary: "Apply a forecast growth or shrinkage factor",
      description:
        "Multiplies a SKU's forecast at a warehouse by a factor over a window (expiry or review date required) and recomputes its safety stock and reorder levels.",
    }),
  list: oc
    .input(ListForecastFactorsInputSchema)
    .output(ListForecastFactorsResponseSchema)
    .route({
      method: "GET",
      summary: "List forecast growth factors for a SKU",
      description:
        "Returns active and historical growth/shrinkage forecast factors for a SKU at one warehouse.",
    }),
  update: oc
    .input(UpdateForecastFactorInputSchema)
    .output(UpdateForecastFactorResponseSchema)
    .route({
      method: "POST",
      summary: "Update a forecast growth factor",
      description:
        "Edits an active growth/shrinkage factor by revoking it and creating a linked replacement in one transaction, then recomputes the SKU's policy levels.",
    }),
  revoke: oc
    .input(RevokeForecastFactorInputSchema)
    .output(RevokeForecastFactorResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a forecast growth factor",
      description:
        "Ends a growth/shrinkage factor immediately so the unadjusted forecast applies again, and recomputes the SKU's policy levels.",
    }),
  preview: oc
    .input(PreviewForecastFactorInputSchema)
    .output(PreviewForecastFactorResponseSchema)
    .route({
      method: "GET",
      summary: "Preview a forecast growth factor",
      description:
        "Shows safety stock, reorder point and order-up-to level before and after a candidate growth/shrinkage factor, without saving it.",
    }),
} as const;
